import { useId } from 'react';
import { Button } from '~/components/buttons';

export const MessageComposer = ({
  value,
  disabled,
  sending,
  masked,
  onChange,
  onSend,
  error,
  label = 'Reply',
  placeholder = 'Type a message',
  sendLabel = 'Send reply',
  inputTag = 'service-message-input',
  sendTag = 'service-send-reply',
  sendDisabled = false,
}: {
  value: string;
  disabled: boolean;
  sending: boolean;
  masked: boolean;
  onChange: (text: string) => void;
  onSend: () => void;
  /** A refused send. Replaces the privacy note so the reason sits beside the draft. */
  error?: string | null;
  /** Accessible name for the message box; not rendered visibly. */
  label?: string;
  placeholder?: string;
  sendLabel?: string;
  inputTag?: string;
  sendTag?: string;
  sendDisabled?: boolean;
}) => {
  const id = useId();
  const noteId = `${id}-note`;
  const submit = () => {
    if (!disabled && !sending && !sendDisabled && value.trim()) onSend();
  };
  return (
    <form
      className="flex flex-col gap-2 border-t border-grey-900 p-4 text-left"
      onSubmit={(event) => {
        event.preventDefault();
        submit();
      }}
    >
      <textarea
        id={id}
        value={value}
        rows={3}
        disabled={disabled}
        aria-label={label}
        aria-describedby={noteId}
        aria-invalid={error ? true : undefined}
        placeholder={placeholder}
        data-cy={inputTag}
        className="w-full resize-y rounded-md border border-grey-900 p-3 text-sm focus-visible:outline-primary-50"
        onChange={(event) => onChange(event.target.value)}
        onKeyDown={(event) => {
          // Enter sends; Shift+Enter keeps a newline. Skip while an IME is composing.
          if (event.key === 'Enter' && !event.shiftKey && !event.nativeEvent.isComposing) {
            event.preventDefault();
            submit();
          }
        }}
      />
      {error ? (
        <p id={noteId} role="alert" className="text-sm text-danger" data-cy={`${inputTag}-error`}>
          {error}
        </p>
      ) : (
        <p id={noteId} className="text-sm text-content-secondary">
          {masked
            ? 'Do not include names or contact information. Messages are screened while identities are masked.'
            : 'Identities are shared for this response only.'}
        </p>
      )}
      <Button
        type="submit"
        size="sm"
        label={sending ? 'Sending…' : sendLabel}
        disabled={disabled || sending || sendDisabled || !value.trim()}
        cyTag={sendTag}
      />
    </form>
  );
};
